// Datentabelle — eine Vorlage für alle Tabellen der neuen Oberfläche.
//   dataTable({ label, columns, rows, sort, cards, footer, rowAttrs, empty })
//   columns: [{ key, label, cell(row) → Inhalt, sortable, rowHeader, num, wide, cls }]
//   sort:    { key, dir: 'asc'|'desc' }  → aria-sort am Kopf, Knopf mit data-vc-sort="key"
//   cards:   'sm' (< 600 px Container) | 'md' (< 840 px) | false — darunter wird jede Zeile zur Karte,
//            die Spaltenköpfe wandern über data-label in die Zellen.
// Die Breite misst der Container (Container-Query), nicht das Fenster: eine Tabelle in einer schmalen Karte
// klappt auch auf großen Bildschirmen zu Karten um.
import { html, attrs, cls } from '../core/h.js';
import { on } from '../core/dom.js';

const SORT_ICON = { asc: 'arrow_upward', desc: 'arrow_downward' };

/** Nächster Sortierzustand beim Klick auf `key`: gleiche Spalte → Richtung drehen, neue Spalte → `firstDir`. */
export function nextSort(cur, key, firstDir = 'desc') {
  if (cur && cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: firstDir };
}

/** Sortiert eine Kopie. `get(row, key)` liefert den Vergleichswert; null/'' landet immer hinten. */
export function sortRows(rows, sort, get = (r, k) => r[k]) {
  if (!sort || !sort.key) return rows.slice();
  const m = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const x = get(a, sort.key), y = get(b, sort.key);
    const ex = x == null || x === '', ey = y == null || y === '';
    if (ex || ey) return ex === ey ? 0 : ex ? 1 : -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * m;
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * m;
  });
}

function head(c, sort) {
  const active = sort && sort.key === c.key;
  const a = attrs({ scope: 'col', class: cls(c.num && 'is-num', c.cls) || null, 'aria-sort': c.sortable ? (active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : null });
  if (!c.sortable) return html`<th${a}>${c.label}</th>`;
  return html`<th${a}><button type="button" class="vc-table-sort" data-vc-sort="${c.key}">${c.label}<span class="vc-icon" aria-hidden="true">${active ? SORT_ICON[sort.dir] : 'unfold_more'}</span></button></th>`;
}

function cell(c, row) {
  const v = c.cell ? c.cell(row) : row[c.key];
  const a = attrs({ 'data-label': c.rowHeader ? null : (c.cardLabel ?? c.label), class: cls(c.num && 'is-num', c.wide && 'is-wide', c.cls) || null });
  return c.rowHeader ? html`<th scope="row"${a}>${v == null || v === '' ? '–' : v}</th>` : html`<td${a}>${v == null || v === '' ? '–' : v}</td>`;
}

export function dataTable({ label, columns, rows, sort = null, cards = 'sm', footer = null, rowAttrs = null, empty = null, cls: extra = '' }) {
  if (!rows.length && empty) return empty;
  return html`<div class="vc-table-wrap${cards ? ' is-cards-' + cards : ''}" tabindex="0" role="region" aria-label="${label}">
    <table class="${cls('vc-table', extra)}">
      <thead><tr>${columns.map((c) => head(c, sort))}</tr></thead>
      <tbody>${rows.map((r) => html`<tr${rowAttrs ? attrs(rowAttrs(r)) : ''}>${columns.map((c) => cell(c, r))}</tr>`)}</tbody>
      ${footer ? html`<tfoot>${footer}</tfoot>` : ''}
    </table></div>`;
}

/** Klick auf einen Sortierkopf → onSort(key). Gibt die Abmeldefunktion zurück. */
export function bindSort(root, onSort) {
  return on(root, 'click', '[data-vc-sort]', (e, b) => onSort(b.dataset.vcSort));
}
